/**
 * Atlas PM · 项目星图 · 顶部信息条（对齐原型 #starehead）
 *
 * 原型：`position:fixed;top:58px;height:64px;z-index:56`，左侧项目名 + 省份·类型，
 * 右侧 6 项指标：健康度 / 整体进度 / 任务 / 已冻结 / 逾期 / 齐套率。
 *
 * ⚠️ P5.1：原型 #starehead 与 #kpibar 同在 top:58，KPI 条(z-index:55) 会盖住信息条；
 *    当时把信息条下移到 top:134。
 *
 * ⚠️ P5.4：已按用户要求去掉星图页 7 张 KPI 卡，故信息条回到 top:58（见 star.css）。
 *    同时去掉信息条内的独立面包屑——项目名改由顶栏 AppShell 面包屑承载
 *    （「全国态势 › 省 › 项目」，全页仅一条）。
 */

import type { ProjectDetail } from '@atlaspm/shared';
import { useActiveProjectTypes } from '../map/useMapData';

export interface StarHeaderProps {
  project: ProjectDetail | undefined;
  taskTotal: number;
  taskFrozen: number;
  taskOverdue: number;
  /**
   * ⭐ P6.54 项目级齐套率（%）；无「需附件要素」时为 null → 显示「—」。
   *   口径与任务级 / 全国态势 KPI 一致（已上传附件的需附件要素数 / 需附件要素总数）。
   */
  deliverableReadyPct: number | null;
  /** 新建任务入口（P3.7b 保留；原型无此按钮，故放在信息条尾端而非工具栏） */
  onCreateTask?: () => void;
  /**
   * ⭐ P6.53：项目编辑入口。
   *   canEditProject —— 是否具备 project:update（OPERATOR 没有，PROVINCE_ADMIN 有）。
   *   无权限时按钮置灰并在旁给出原因，避免「点了报 403」。
   */
  canEditProject?: boolean;
  onEditProject?: () => void;
}

/** 健康度色（与地图/星图语义一致：≥85 绿 / 60~85 琥珀 / <60 玫红） */
function healthColor(score: number): string {
  if (score >= 85) return 'var(--ok)';
  if (score >= 60) return 'var(--warn)';
  return 'var(--alert)';
}

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

export default function StarHeader({
  project,
  taskTotal,
  taskFrozen,
  taskOverdue,
  deliverableReadyPct,
  onCreateTask,
  canEditProject = false,
  onEditProject,
}: StarHeaderProps) {
  const health = project?.healthScore ?? null;
  const progress = project?.progressPct ?? 0;

  /**
   * ⭐ 项目类型中文名。
   *
   * P6.37 修好了地图页（当时是「地图页只调 useMapData，从不触发 useProjectTypes 的副作用，
   * 导致 effectiveProjectTypes() 恒回退硬编码常量 ⇒ 查不到新字典 code ⇒ 显示 PT_xxx」），
   * 但星图页同样只调 useStarData、从未加载字典，于是同一个缺陷在星图顶部信息条复现。
   *
   * 这里直接复用地图页的 useActiveProjectTypes()：queryKey 与 useProjectTypes 一致，
   * 共享 TanStack 缓存，不会产生重复请求。
   * 字典未加载完时回退渲染原始值（避免闪烁成空白）。
   */
  const projectTypesQ = useActiveProjectTypes();
  const typeName = (() => {
    const raw = project?.projectType;
    if (!raw) return null;
    const list = projectTypesQ.data ?? [];
    return list.find((t) => t.code === raw)?.name ?? raw;
  })();

  return (
    <header className="star-head">
      <span className="star-head__name">
        <h2>{project?.name ?? '项目星图'}</h2>
        {project?.provinceName && <span className="star-head__province">{project.provinceName}</span>}
      </span>

      {project && typeName && <span className="star-head__badge">{typeName}</span>}

      {/* ⭐ P6.53：项目编辑入口（无 project:update 权限时置灰并说明原因，绝不点了报 403） */}
      {project && onEditProject && (
        <>
          <button
            type="button"
            className="star-head__edit"
            data-testid="star-project-edit"
            disabled={!canEditProject}
            title={
              canEditProject
                ? '编辑项目：名称 / 负责人 / 省份 / 类型 / 起止时间 / 备注'
                : undefined
            }
            onClick={onEditProject}
          >
            ✎ 编辑项目
          </button>
          {!canEditProject && (
            <span className="star-head__edit-hint" data-testid="star-project-edit-hint">
              需 project:update 权限（省管理员及以上）
            </span>
          )}
        </>
      )}

      <div className="star-head__metrics">
        <div className="star-head__metric">
          <div className="l">健康度</div>
          <div className="v" style={health === null ? undefined : { color: healthColor(health) }}>
            {health === null ? '—' : health.toFixed(1)}
          </div>
        </div>
        <div className="star-head__metric">
          <div className="l">整体进度</div>
          <div className="v">{fmtPct(progress)}</div>
        </div>
        <div className="star-head__metric">
          <div className="l">任务</div>
          <div className="v">{taskTotal}</div>
        </div>
        <div className="star-head__metric">
          <div className="l">已冻结</div>
          <div className="v" style={{ color: 'var(--frozen)' }}>
            {taskFrozen}
          </div>
        </div>
        <div className="star-head__metric">
          <div className="l">逾期</div>
          <div className="v" style={{ color: taskOverdue > 0 ? 'var(--alert)' : 'var(--ok)' }}>
            {taskOverdue}
          </div>
        </div>
        <div className="star-head__metric">
          {/* ⭐ P6.54：接真实数据（按「需附件要素」核算的项目级齐套率）。
              无任何需附件要素时为 null → 显示「—」，绝不显示 0%。 */}
          <div className="l">齐套率</div>
          <div className="v" data-testid="star-completeness">
            {deliverableReadyPct === null ? '—' : fmtPct(deliverableReadyPct)}
          </div>
        </div>
      </div>

      {onCreateTask && (
        <button type="button" className="star-head__cta" onClick={onCreateTask} data-testid="new-task-entry">
          ＋ 新建任务
        </button>
      )}
    </header>
  );
}
